// Rummy hand — bottom strip of player's cards with drag-drop engine.
// Drop zones ("drop-zone" class):
//   .rummy-hand__group     — move card into an existing group
//   #rummy-discard-pile    — discard
//   #rummy-declare-slot    — declare (tuck the 14th card face-down)
//   #rummy-hand            — drop to an empty new group

import { useRef, useState } from 'react';
import Card, { type CardData } from './Card';
import { playDrawClosed, playSort } from './sounds';

type Props = {
  groups: CardData[][];                // ordered groups of cards
  wildRank: string | null;
  yourTurn: boolean;
  // Drop action callbacks — parent owns game-state; we just report intents.
  onDropToDiscard: (cardId: string) => void;
  onDropToDeclare: (cardId: string) => void;
  onRegroup: (newGroups: CardData[][]) => void;
};

export default function HandStrip({
  groups, wildRank, yourTurn, onDropToDiscard, onDropToDeclare, onRegroup,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const dragState = useRef<{
    cardId: string | null;
    isDragging: boolean;
    clone: HTMLDivElement | null;
    startX: number; startY: number;
    offsetX: number; offsetY: number;
    currentZone: HTMLElement | null;
  }>({
    cardId: null, isDragging: false, clone: null,
    startX: 0, startY: 0, offsetX: 0, offsetY: 0, currentZone: null,
  });

  function handlePointerDown(e: React.PointerEvent, cardId: string) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const cardEl = document.getElementById(cardId) as HTMLElement | null;
    if (!cardEl) return;
    const rect = cardEl.getBoundingClientRect();
    dragState.current = {
      cardId,
      isDragging: false,
      clone: null,
      startX: e.clientX, startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      currentZone: null,
    };
    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    try { (cardEl as any).setPointerCapture?.(e.pointerId); } catch {}
  }

  function onPointerMove(e: PointerEvent) {
    const s = dragState.current;
    if (!s.cardId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.isDragging && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      s.isDragging = true;
      initDragVisuals();
    }
    if (!s.isDragging || !s.clone) return;
    e.preventDefault();
    s.clone.style.left = `${e.clientX - s.offsetX}px`;
    s.clone.style.top  = `${e.clientY - s.offsetY}px`;
    s.clone.style.visibility = 'hidden';
    const below = document.elementFromPoint(e.clientX, e.clientY);
    s.clone.style.visibility = 'visible';
    const zone = (below && (below as HTMLElement).closest('.drop-zone')) as HTMLElement | null;
    if (s.currentZone && s.currentZone !== zone) s.currentZone.classList.remove('drag-over');
    if (zone) zone.classList.add('drag-over');
    s.currentZone = zone;
  }

  function initDragVisuals() {
    const s = dragState.current;
    if (!s.cardId) return;
    const cardEl = document.getElementById(s.cardId) as HTMLElement | null;
    if (!cardEl) return;
    playDrawClosed();
    const clone = cardEl.cloneNode(true) as HTMLDivElement;
    clone.id = 'rummy-drag-clone';
    const rect = cardEl.getBoundingClientRect();
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.position = 'fixed';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.style.transform = 'scale(1.05) rotate(3deg)';
    clone.style.boxShadow = '0 20px 40px rgba(0,0,0,0.7)';
    clone.style.transition = 'none';
    document.body.appendChild(clone);
    cardEl.style.opacity = '0.2';
    s.clone = clone;
  }

  function onPointerUp(_e: PointerEvent) {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerUp);
    const s = dragState.current;
    if (!s.cardId) return;
    const cardEl = document.getElementById(s.cardId) as HTMLElement | null;
    if (cardEl) cardEl.style.opacity = '1';
    if (s.isDragging) {
      if (s.clone) { s.clone.remove(); s.clone = null; }
      if (s.currentZone) {
        s.currentZone.classList.remove('drag-over');
        routeDrop(s.currentZone, s.cardId);
        s.currentZone = null;
      }
    } else {
      toggleSelect(s.cardId);
    }
    dragState.current = {
      cardId: null, isDragging: false, clone: null,
      startX: 0, startY: 0, offsetX: 0, offsetY: 0, currentZone: null,
    };
  }

  function routeDrop(zone: HTMLElement, cardId: string) {
    if (zone.id === 'rummy-discard-pile') onDropToDiscard(cardId);
    else if (zone.id === 'rummy-declare-slot') onDropToDeclare(cardId);
    else if (zone.classList.contains('rummy-hand__group')) moveToGroup(zone, cardId);
    else if (zone.id === 'rummy-hand') moveToNewGroup(cardId);
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function extract(cardId: string): { card: CardData | null; next: CardData[][] } {
    let card: CardData | null = null;
    const next = groups
      .map((g) => g.filter((c) => {
        if (c.id === cardId) { card = c; return false; }
        return true;
      }))
      .filter((g) => g.length > 0);
    return { card, next };
  }

  function moveToGroup(zone: HTMLElement, cardId: string) {
    // Find the target group by looking at the first card in it.
    const firstCardEl = zone.querySelector('.rummy-card') as HTMLElement | null;
    const targetFirstId = firstCardEl?.id;
    const targetGroupIdx = groups.findIndex((g) => g.some((c) => c.id === targetFirstId));
    const { card, next } = extract(cardId);
    if (!card) return;
    if (targetGroupIdx >= 0) {
      next[targetGroupIdx] = [...next[targetGroupIdx], card];
      onRegroup(next);
    } else {
      next.push([card]);
      onRegroup(next);
    }
  }

  function moveToNewGroup(cardId: string) {
    const { card, next } = extract(cardId);
    if (!card) return;
    next.push([card]);
    onRegroup(next);
  }

  // Sort + group helpers — exported via window for Page.tsx buttons.
  // (Keeps the component decoupled from the parent action bar.)
  if (typeof window !== 'undefined') {
    (window as any).__rummySort = () => {
      playSort();
      const flat = groups.flat();
      flat.sort((a, b) => {
        if (a.suit !== b.suit) return (a.suit || '').localeCompare(b.suit || '');
        return (a.value ?? 0) - (b.value ?? 0);
      });
      // Rebuild into per-suit groups.
      const out: CardData[][] = [];
      let cur: CardData[] = [];
      let curSuit: string | null = null;
      for (const c of flat) {
        if (c.suit !== curSuit) { if (cur.length) out.push(cur); cur = []; curSuit = c.suit; }
        cur.push(c);
      }
      if (cur.length) out.push(cur);
      onRegroup(out);
      setSelected(new Set());
    };
    (window as any).__rummyGroupSelected = () => {
      if (selected.size < 2) return;
      playSort();
      const picked: CardData[] = [];
      const rest: CardData[][] = [];
      for (const g of groups) {
        const leftover: CardData[] = [];
        for (const c of g) {
          if (selected.has(c.id)) picked.push(c);
          else leftover.push(c);
        }
        if (leftover.length) rest.push(leftover);
      }
      if (picked.length) rest.push(picked);
      onRegroup(rest);
      setSelected(new Set());
    };
  }

  return (
    <div className="rummy-hand-wrap">
      <div id="rummy-hand" className="rummy-hand drop-zone">
        {groups.map((g, gi) => (
          <div key={gi} className="rummy-hand__group drop-zone">
            {g.map((card) => (
              <Card
                key={card.id}
                card={card}
                selected={selected.has(card.id)}
                wild={!!wildRank && (card.rank === wildRank || card.suit === 'joker')}
                onPointerDown={yourTurn ? (e) => handlePointerDown(e, card.id) : undefined}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
